/**
 * ルートプランのタイムライン
 *
 * 現地で見ながら動くことを前提に、時刻・移動・スタンプ操作を 1 列に並べる。
 * 並べ替えは専用ハンドルのドラッグで行う（行全体を掴めるとスクロールと競合するため）。
 * 出発・到着行もドロップ先になっていて、経由地を端点へ引き上げたり逆に降ろしたりできる。
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AlertTriangle, Clock, GripVertical, MapPin, Pin, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import type { PlanEndpointRole, PlanItemRallyLink, RoutePlanItem } from '@/api/types/route-plan'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

import { PlaceMapsLink } from '../../components/place-maps-link'
import { desiredArrivalLabel, formatDistance, formatDuration, hasRally, isFullyStamped } from '../../lib'
import { PlanDayHoursChip } from './plan-day-hours-chip'
import { StampButton } from './plan-stamp-button'

// 端点はソート対象ではなく単独のドロップ先なので、行の id とぶつからない文字列を振る
const SLOT_ID: Record<PlanEndpointRole, string> = { origin: 'origin-slot', dest: 'dest-slot' }
const DRAG_ID: Record<PlanEndpointRole, string> = { origin: 'origin-drag', dest: 'dest-drag' }

const roleOfSlot = (id: string | number): PlanEndpointRole | null =>
  id === SLOT_ID.origin ? 'origin' : id === SLOT_ID.dest ? 'dest' : null

const roleOfDrag = (id: string | number): PlanEndpointRole | null =>
  id === DRAG_ID.origin ? 'origin' : id === DRAG_ID.dest ? 'dest' : null

const WARNING_LABEL: Record<NonNullable<RoutePlanItem['warning']>, string> = {
  closed: '営業時間外',
  closing_soon: '閉店間近',
}

const formatTime = (value: string | null): string =>
  value ? parseUtcDate(value).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }) : '--:--'

const travelLabel = (item: RoutePlanItem): string | null => {
  const duration = formatDuration(item.travel_duration_sec)
  const distance = formatDistance(item.travel_distance_m)
  if (!duration && !distance) return null
  return [duration, distance].filter(Boolean).join(' / ')
}

// Google の住所は「日本、〒000-0000 」から始まる。狭い画面では邪魔なだけなので落とす
const shortAddress = (address: string): string => address.replace(/^日本、\s*/, '').replace(/^〒\d{3}-\d{4}\s*/, '')

/** 滞在時間と到着希望を開くチップ。行本体の選択と区別するため別ボタンにする */
export const StaySettingChip = ({
  item,
  readOnly,
  onEdit,
}: {
  item: RoutePlanItem
  readOnly: boolean
  onEdit: (item: RoutePlanItem) => void
}) => {
  const arrival = desiredArrivalLabel(item)
  const className = cn(
    'flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs tabular-nums',
    arrival ? 'border-blue-500/50 text-blue-600 dark:text-blue-400' : 'border-border text-muted-foreground',
  )
  const content = (
    <>
      <Clock size={11} />
      {arrival ? `${arrival} ・ ${item.stay_minutes}分` : `${item.stay_minutes}分`}
    </>
  )
  // 共有されたプランでは滞在時間を見せるだけにして、編集の導線は出さない
  if (readOnly) return <span className={className}>{content}</span>
  return (
    <button onClick={() => onEdit(item)} aria-label="滞在時間と到着希望を設定" className={className}>
      {content}
    </button>
  )
}

const WarningBadge = ({ warning }: { warning: RoutePlanItem['warning'] }) =>
  warning === null ? null : (
    <Badge variant="destructive" className="shrink-0 gap-1">
      <AlertTriangle size={11} />
      {WARNING_LABEL[warning]}
    </Badge>
  )

const RequiredMark = ({ item }: { item: RoutePlanItem }) =>
  item.is_required ? <Pin size={13} className="shrink-0 text-violet-600" aria-label="必ず立ち寄る" /> : null

/** 出発・帰着の行。端点にスポットを充てているときはそこからスタンプを押せる */
type EndpointRowProps = {
  role: PlanEndpointRole
  time: string
  item: RoutePlanItem | null
  /** この端点そのものに充てたスポット。あればハンドルを掴んで経由地へ降ろせる */
  pinnedItem: RoutePlanItem | null
  fallbackIcon: 'dot' | 'pin'
  isReordering: boolean
  /** 共有されたプラン。スタンプ以外の操作を出さない */
  readOnly: boolean
  onToggleStamp: (item: RoutePlanItem, links: PlanItemRallyLink[], stamped: boolean) => void
  onEdit: (item: RoutePlanItem) => void
  children: ReactNode
}

const EndpointRow = ({
  role,
  time,
  item,
  pinnedItem,
  fallbackIcon,
  isReordering,
  readOnly,
  onToggleStamp,
  onEdit,
  children,
}: EndpointRowProps) => {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: SLOT_ID[role], disabled: isReordering || readOnly })
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: DRAG_ID[role],
    disabled: pinnedItem === null || isReordering || readOnly,
  })

  return (
    <div
      ref={setDropRef}
      className={cn(
        'flex items-center rounded-lg transition-colors',
        isOver && 'bg-accent ring-1 ring-primary/50',
        isDragging && 'opacity-50',
      )}
    >
      {pinnedItem && !readOnly ? (
        <div
          ref={setDragRef}
          className="flex w-5 shrink-0 touch-none items-center justify-center self-stretch text-muted-foreground/60 active:cursor-grabbing"
          aria-label="ドラッグして経由地に移す"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={15} />
        </div>
      ) : (
        <span className="w-5 shrink-0" />
      )}

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{time}</span>
        {item && hasRally(item) ? (
          <StampButton item={item} onToggle={onToggleStamp} />
        ) : fallbackIcon === 'dot' ? (
          <span className="size-2 shrink-0 rounded-full bg-blue-500" />
        ) : (
          <MapPin size={14} className="shrink-0 text-blue-500" />
        )}
        <span className="min-w-0 flex-1 text-sm">{children}</span>
        {item && <StaySettingChip item={item} readOnly={readOnly} onEdit={onEdit} />}
        <PlanDayHoursChip dayHours={item?.day_hours ?? null} />
        <WarningBadge warning={item?.warning ?? null} />
        {item && <PlaceMapsLink item={item} />}
      </div>
    </div>
  )
}

type RowProps = {
  item: RoutePlanItem
  number: number
  isSelected: boolean
  isReordering: boolean
  /** 絞り込んでいるあいだは前の行と隣り合っていないので、移動時間は出さない */
  isFiltered: boolean
  /** 共有されたプラン。スタンプ以外の操作を出さない */
  readOnly: boolean
  onSelect: (item: RoutePlanItem) => void
  onEdit: (item: RoutePlanItem) => void
  onToggleStamp: (item: RoutePlanItem, links: PlanItemRallyLink[], stamped: boolean) => void
  onRemove: (item: RoutePlanItem) => void
}

const TimelineRow = ({
  item,
  number,
  isSelected,
  isReordering,
  isFiltered,
  readOnly,
  onSelect,
  onEdit,
  onToggleStamp,
  onRemove,
}: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: isReordering || isFiltered || readOnly,
  })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && 'relative z-10 opacity-70')}
    >
      {!isFiltered && travelLabel(item) && (
        <div className="flex items-center gap-2 py-1 pl-5">
          <span className="w-10 shrink-0" />
          <span className="ml-[3px] h-6 w-px shrink-0 bg-border" />
          <span className="text-xs text-muted-foreground">{travelLabel(item)}</span>
        </div>
      )}

      <div
        className={cn(
          'flex items-center rounded-lg transition-colors',
          isSelected ? 'bg-accent' : 'hover:bg-accent/40',
        )}
      >
        {readOnly ? (
          <span className="w-5 shrink-0" />
        ) : (
          <div
            className="flex w-5 shrink-0 touch-none items-center justify-center self-stretch text-muted-foreground/60 active:cursor-grabbing"
            aria-label="ドラッグして並べ替え"
            {...attributes}
            {...listeners}
          >
            <GripVertical size={15} />
          </div>
        )}

        <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {formatTime(item.arrival_at)}
        </span>
        <span
          className={cn(
            'ml-1.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white',
            isFullyStamped(item) ? 'bg-emerald-500' : 'bg-orange-500',
          )}
        >
          {number}
        </span>

        {hasRally(item) && (
          <span className="ml-2 flex shrink-0">
            <StampButton item={item} onToggle={onToggleStamp} />
          </span>
        )}

        <div className="min-w-0 flex-1 space-y-1 py-2 pl-2">
          <button onClick={() => onSelect(item)} className="flex w-full items-center gap-2 p-0 text-left">
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{item.name}</span>
            <RequiredMark item={item} />
            <WarningBadge warning={item.warning} />
          </button>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <StaySettingChip item={item} readOnly={readOnly} onEdit={onEdit} />
            <PlanDayHoursChip dayHours={item.day_hours} />
            {item.address && <span className="min-w-0 flex-1 truncate">{shortAddress(item.address)}</span>}
          </div>
        </div>

        <PlaceMapsLink item={item} />

        {!readOnly && (
          <Button
            size="sm"
            variant="ghost"
            className="size-7 shrink-0 p-0! text-muted-foreground"
            disabled={isReordering}
            onClick={() => onRemove(item)}
            aria-label="プランから外す"
          >
            <Trash2 size={15} />
          </Button>
        )}
      </div>
    </div>
  )
}

type Props = {
  items: RoutePlanItem[]
  /** 行に出す番号。絞り込んでもルートの何番目かを失わないために外からもらう */
  numberOf: (item: RoutePlanItem) => number
  /** 絞り込み・並べ替え中。並びがルートと違うので D&D と移動時間を伏せる */
  isFiltered: boolean
  originName: string | null
  returnToOrigin: boolean
  destName: string | null
  /** 出発地そのものに充てたスポット */
  originItem: RoutePlanItem | null
  /** 到着地そのものに充てたスポット */
  destItem: RoutePlanItem | null
  departAt: string | null
  totalDurationSec: number | null
  totalDistanceM: number | null
  selectedId: number | null
  isReordering: boolean
  /** 共有されたプラン。スタンプ以外の操作を出さない */
  readOnly?: boolean
  onSelect: (item: RoutePlanItem) => void
  onEdit: (item: RoutePlanItem) => void
  onToggleStamp: (item: RoutePlanItem, links: PlanItemRallyLink[], stamped: boolean) => void
  onReorder: (placeIds: number[]) => void
  /** 経由地を端点へ引き上げる */
  onPinEndpoint: (role: PlanEndpointRole, placeId: number) => void
  /** 端点のスポットを経由地へ降ろす。place_ids は降ろしたあとの並び */
  onUnpinEndpoint: (role: PlanEndpointRole, placeIds: number[]) => void
  onRemove: (item: RoutePlanItem) => void
}

export const PlanTimeline = ({
  items,
  numberOf,
  isFiltered,
  originName,
  returnToOrigin,
  destName,
  originItem,
  destItem,
  departAt,
  totalDurationSec,
  totalDistanceM,
  selectedId,
  isReordering,
  readOnly = false,
  onSelect,
  onEdit,
  onToggleStamp,
  onReorder,
  onPinEndpoint,
  onUnpinEndpoint,
  onRemove,
}: Props) => {
  const totalLabel = [formatDuration(totalDurationSec), formatDistance(totalDistanceM)].filter(Boolean).join(' / ')
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  // 並びがルートどおりのときだけ並べ替えられる
  const isLocked = isReordering || isFiltered || readOnly
  // 帰着なら出発地と同じスポットなので、両方の行から同じスタンプを押せる
  const endItem = returnToOrigin ? originItem : destItem
  // 端点にスポットを充てると plan の origin_name / dest_name は古いままなので、スポット名を優先する
  const originLabel = originItem?.name ?? originName ?? '出発地'
  const destLabel = destItem?.name ?? destName
  // 帰着・自由入力の到着地は行を持たないので、出発時刻に所要時間を足して出す
  const endAt =
    destItem?.arrival_at ??
    (departAt && totalDurationSec !== null
      ? new Date(parseUtcDate(departAt).getTime() + totalDurationSec * 1000).toISOString()
      : null)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const overRole = roleOfSlot(over.id)
    const activeRole = roleOfDrag(active.id)

    if (activeRole !== null) {
      const moved = activeRole === 'origin' ? originItem : destItem
      if (moved === null) return
      // 反対側の端点へ落としたら役割を入れ替えるだけ。pin 側が元の端点を外してくれる
      if (overRole !== null) {
        if (overRole !== activeRole) onPinEndpoint(overRole, moved.place_id)
        return
      }
      const to = items.findIndex((item) => item.id === over.id)
      const placeIds = items.map((item) => item.place_id)
      placeIds.splice(to === -1 ? placeIds.length : to, 0, moved.place_id)
      onUnpinEndpoint(activeRole, placeIds)
      return
    }

    if (overRole !== null) {
      const moved = items.find((item) => item.id === active.id)
      if (moved === undefined) return
      onPinEndpoint(overRole, moved.place_id)
      return
    }

    const from = items.findIndex((item) => item.id === active.id)
    const to = items.findIndex((item) => item.id === over.id)
    if (from === -1 || to === -1) return
    onReorder(arrayMove(items, from, to).map((item) => item.place_id))
  }

  return (
    <div className="space-y-1 px-2 py-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <EndpointRow
          role="origin"
          time={formatTime(departAt)}
          item={originItem}
          pinnedItem={originItem}
          fallbackIcon="dot"
          isReordering={isLocked}
          readOnly={readOnly}
          onToggleStamp={onToggleStamp}
          onEdit={onEdit}
        >
          {originLabel}を出発
        </EndpointRow>

        <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
          {items.map((item) => (
            <TimelineRow
              key={item.id}
              item={item}
              number={numberOf(item)}
              isSelected={item.id === selectedId}
              isReordering={isReordering}
              isFiltered={isFiltered}
              readOnly={readOnly}
              onSelect={onSelect}
              onEdit={onEdit}
              onToggleStamp={onToggleStamp}
              onRemove={onRemove}
            />
          ))}
        </SortableContext>

        {isFiltered && items.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">条件に合うスポットはありません</p>
        )}

        {destItem && travelLabel(destItem) && (
          <div className="flex items-center gap-2 py-1 pl-5">
            <span className="w-10 shrink-0" />
            <span className="ml-[3px] h-6 w-px shrink-0 bg-border" />
            <span className="text-xs text-muted-foreground">{travelLabel(destItem)}</span>
          </div>
        )}

        <div className="pt-1">
          <EndpointRow
            role="dest"
            time={endAt ? formatTime(endAt) : ''}
            item={endItem}
            pinnedItem={destItem}
            fallbackIcon="pin"
            isReordering={isLocked}
            readOnly={readOnly}
            onToggleStamp={onToggleStamp}
            onEdit={onEdit}
          >
            {returnToOrigin ? `${originLabel}に帰着` : destLabel ? `${destLabel}に到着` : '到着地なし'}
            {totalLabel && <span className="ml-1 text-xs text-muted-foreground">（計 {totalLabel}）</span>}
          </EndpointRow>
        </div>
      </DndContext>
    </div>
  )
}
